import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseNumber } from "./numberInput";
import { fmtMoney, CURRENCY_CHOICES } from "./money";
import { amortize, flatRateLoan, formatMonthsSaved } from "./loan";
import { paymentDate, ymdToISO, formatMonthYear, formatFullDate } from "./paymentDates";
import { loanFromQuery, loanToQuery } from "./loanShare";
import { FirstPaymentDateField, PayoffDateNotice, useFirstPaymentDate } from "./FirstPaymentDateField";

// Working, scenarios, schedule table and exports load on demand, so the tool opens as light as before.
const LoanExtras = lazy(() => import("./LoanExtras"));
const LoanWorkbench = lazy(() => import("./LoanWorkbench"));

const TOOL_INFO_CONTENT = {
  what: `Calculates the monthly payment, total interest and full amortization schedule for a loan from the principal, interest rate and term, with an optional extra monthly payment that shows how much interest and time you save. Every payment in the schedule, and the payoff date, uses real calendar dates that start from your first payment date.`,
  how: `Enter the loan amount, interest rate and term (in years or months), and choose a currency. Add an extra monthly payment to see the payoff date move earlier. Set the first payment date to the day your first payment is due (it starts as the 1st of next month) to see the real date of every payment and the payoff date. It only labels the schedule and does not change the amounts. Expand the schedule for the principal/interest split of every payment, or download it, dates included, as a CSV file.`,
  tip: `Even a small difference in interest rate makes a real difference in total interest paid over a long loan, so compare the total cost, not just the monthly payment. In the early years most of each payment is interest, which is why extra principal payments early on save the most.`,
};

export default function LoanCalculator() {
  const [init] = useState(() => (typeof window === "undefined" ? loanFromQuery("") : loanFromQuery(window.location.search, CURRENCY_CHOICES.map((c) => c.code))));
  const [principal, setPrincipal] = useState(init.principal);
  const [rate, setRate] = useState(init.rate);
  const [term, setTerm] = useState(init.term);
  const [unit, setUnit] = useState<"years" | "months">(init.unit);
  const [extra, setExtra] = useState(init.extra);
  const [currency, setCurrency] = useState(init.currency);
  const [method, setMethod] = useState<"reducing" | "flat">(init.method);
  // Shareable link: the typed numbers only (replaceState, no history entries, nothing stored).
  useEffect(() => {
    try {
      const q = loanToQuery({ principal, rate, term, unit, extra, currency, method });
      window.history.replaceState(null, "", `${window.location.pathname}${q ? `?${q}` : ""}${window.location.hash}`);
    } catch { /* ignore */ }
  }, [principal, rate, term, unit, extra, currency, method]);
  const fp = useFirstPaymentDate();
  const first = fp.first;

  const p = parseNumber(principal), rt = parseNumber(rate), t = parseNumber(term), ex = parseNumber(extra) ?? 0;
  const months = t == null ? NaN : Math.round(unit === "years" ? t * 12 : t);
  const error =
    p == null || p <= 0 ? "Enter a loan amount greater than 0."
    : rt == null || rt < 0 || rt > 100 ? "Enter an interest rate between 0% and 100%."
    : !Number.isFinite(months) || months < 1 ? "Enter a term of at least 1 month."
    : months > 1200 ? "The term can be at most 100 years."
    : ex < 0 ? "The extra payment cannot be negative." : "";

  const res = useMemo(() => (error ? null : method === "flat" ? flatRateLoan(p!, rt!, months) : amortize({ principal: p!, annualPct: rt!, months, extra: ex })), [error, p, rt, months, ex, method]);
  const paidInMonthOne = !!res && method === "reducing" && res.rows.length === 1 && months > 1;
  // A result is always shown; a use is charged only when the output is taken (copy, print, save, CSV).
  const { gate } = useLogToolUsage(!!res);
  const m = useCallback((n: number) => fmtMoney(n, currency), [currency]);
  const lastPay = res && res.rows.length > 0 ? paymentDate(first, res.rows.length - 1) : null;
  const originalEnd = !error ? paymentDate(first, months - 1) : null;

  const inputs = useMemo(() => (res ? { principal: p!, annualPct: rt!, months, extra: method === "reducing" ? ex : 0, method } : null), [res, p, rt, months, ex, method]);

  const main = useMemo(() => (res && method === "reducing" ? { principal: p!, annualPct: rt!, years: months / 12 } : null), [res, method, p, rt, months]);

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Loan Amount</Label><Input inputMode="decimal" aria-label="Loan amount" value={principal} onChange={(e) => setPrincipal(e.target.value)} /></div>
            <div><Label>Currency</Label>
              <select className="border rounded px-2 py-2 w-full bg-background text-sm" aria-label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCY_CHOICES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select></div>
          </div>
          <div><Label>Annual Interest Rate (%)</Label><Input inputMode="decimal" aria-label="Interest rate" value={rate} onChange={(e) => setRate(e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Loan Term</Label><Input inputMode="decimal" aria-label="Loan term" value={term} onChange={(e) => setTerm(e.target.value)} /></div>
            <div><Label>&nbsp;</Label>
              <select className="border rounded px-2 py-2 w-full bg-background text-sm" aria-label="Term unit" value={unit} onChange={(e) => setUnit(e.target.value as "years" | "months")}>
                <option value="years">years</option><option value="months">months</option>
              </select></div>
          </div>
          <div><Label>Interest method</Label>
            <select className="border rounded px-2 py-2 w-full bg-background text-sm" aria-label="Interest method" value={method} onChange={(e) => setMethod(e.target.value as "reducing" | "flat")}>
              <option value="reducing">Reducing balance (standard EMI)</option><option value="flat">Flat rate (interest on the original amount)</option>
            </select></div>
          <FirstPaymentDateField id="ln-first-payment" state={fp} help="The date your first payment is due. Later payments fall on the same day each month. It only puts dates on the schedule and the payoff; it does not change your payment or interest, because every period is treated as a full month. Clear it to use the 1st of next month." />
          {method === "reducing" && <div><Label>Extra monthly payment (optional)</Label><Input inputMode="decimal" aria-label="Extra payment" value={extra} onChange={(e) => setExtra(e.target.value)} /></div>}
          {error && <p className="text-sm text-red-600" data-testid="loan-error">{error}</p>}
        </div>
        <div className="space-y-4">
          <div className="p-6 rounded-xl border bg-card text-center">
            <p className="text-sm text-muted-foreground">Monthly Payment</p>
            <p className="text-3xl font-bold text-primary" data-testid="loan-payment">{!res ? "—" : m(res.payment)}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg border bg-card text-center"><p className="text-xs text-muted-foreground">Total Payment</p><p className="text-lg font-semibold" data-testid="loan-total">{!res ? "—" : m(res.totalPaid)}</p></div>
            <div className="p-4 rounded-lg border bg-card text-center"><p className="text-xs text-muted-foreground">Total Interest</p><p className="text-lg font-semibold" data-testid="loan-interest">{!res ? "—" : m(res.totalInterest)}</p></div>
          </div>
          {lastPay && <PayoffDateNotice first={first} payments={res!.rows.length} testId="loan-payoff" />}
          {paidInMonthOne && <p className="text-sm text-center p-3 rounded-lg border bg-primary/5" data-testid="loan-paid-m1">With this extra payment the loan is paid off in month 1. The monthly payment shown is the regular scheduled amount.</p>}
          {method === "flat" && <p className="text-xs text-center text-muted-foreground">Flat rate: interest is charged on the full original amount for the whole term, so the true annual cost is higher than the quoted rate.</p>}
          {res && res.monthsSaved + res.interestSaved > 0 && ex > 0 && (
            <p className="text-sm text-center p-3 rounded-lg border bg-primary/5" data-testid="loan-saved">
              With {m(ex)} extra each month you finish {formatMonthsSaved(res.monthsSaved)} early and save {m(res.interestSaved)} in interest.
              {lastPay && originalEnd && <span data-testid="loan-extra-dates"> New payoff date {formatMonthYear(lastPay)} instead of {formatMonthYear(originalEnd)}.</span>}
            </p>
          )}
        </div>
      </div>

      {res && inputs && res.rows.length > 0 && (
        <Suspense fallback={<p className="mt-8 text-sm text-muted-foreground" role="status">Loading schedule and working...</p>}>
          <LoanExtras inputs={inputs} res={res} first={first} money={m} gate={gate} />
        </Suspense>
      )}
      {method === "reducing" && (
        <Suspense fallback={null}>
          <LoanWorkbench main={main} first={first} money={m} gate={gate} />
        </Suspense>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
